import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import {
  MatDialogRef,
  MAT_DIALOG_DATA,
  MatDialogModule,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDividerModule } from '@angular/material/divider';
import { LoginService } from '../../../services/login.service';
import { MasterDataService } from '../../../services/master-data.service';
import { MasterPermission } from '../../../interfaces/master-permission';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';

export interface InviteDialogData {
  orgName: string;
  isSuperAdmin?: boolean;
  organizations?: { id: number; org_name: string }[];
  targetOrgId?: number;
  hideGlobalPermissions?: boolean;
}

/**
 * Dialog component for inviting a new user to the organization.
 *
 * Collects an email address and a set of initial permissions, then calls
 * the invite endpoint which creates a pending user and emails an activation
 * link valid for 48 hours. On completion the link is also displayed in the
 * dialog so the admin can share it manually if needed.
 *
 * @author julian.f.verhey
 */
@Component({
  selector: 'app-invite-user',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatButtonModule,
    MatInputModule,
    MatFormFieldModule,
    MatCheckboxModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDividerModule,
    MatAutocompleteModule,
    MatSelectModule,
  ],
  templateUrl: './invite-user.component.html',
  styleUrl: './invite-user.component.scss',
})
export class InviteUserComponent implements OnInit {
  form: FormGroup;
  permissions: MasterPermission[] = [];
  selectedPermissionIds: Set<number> = new Set();
  selectedOrgId: number | null = null;
  sending = false;
  sent = false;
  inviteLink = '';
  error = '';

  masterPermissions: MasterPermission[] = [];

  private readonly GLOBAL_PERMISSIONS = ['report.global', 'system.admin'];
  /** Desired prefix order for the permission groups. */
  private readonly GROUP_ORDER = ['ticket', 'user', 'report'];

  isOrgScoped(perm: MasterPermission): boolean {
    return !this.GLOBAL_PERMISSIONS.includes(perm.mpm_type);
  }
  onOrgChange(): void {
    if (!this.selectedOrgId && !this.data.targetOrgId) {
      for (const perm of this.permissions) {
        if (this.isOrgScoped(perm)) {
          this.selectedPermissionIds.delete(perm.id);
        }
      }
    }
  }

  /**
   * Permissions grouped by their dot-prefix (e.g. "ticket", "user", "report")
   * and sorted by the GROUP_ORDER list. Unknown prefixes appear last, alphabetically.
   */
  get permissionGroups(): { label: string; permissions: MasterPermission[] }[] {
    const GLOBAL_PERMISSIONS = ['report.global', 'system.admin'];

    const filtered = this.data.hideGlobalPermissions
      ? this.permissions.filter((p) => !GLOBAL_PERMISSIONS.includes(p.mpm_type))
      : this.permissions;

    const groups = new Map<string, MasterPermission[]>();
    for (const perm of filtered) {
      const prefix = perm.mpm_type.split('.')[0] ?? 'other';
      if (!groups.has(prefix)) groups.set(prefix, []);
      groups.get(prefix)!.push(perm);
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => {
        const ai = this.GROUP_ORDER.indexOf(a);
        const bi = this.GROUP_ORDER.indexOf(b);
        if (ai === -1 && bi === -1) return a.localeCompare(b);
        if (ai === -1) return 1;
        if (bi === -1) return -1;
        return ai - bi;
      })
      .map(([prefix, permissions]) => ({ label: prefix, permissions }));
  }

  constructor(
    private fb: FormBuilder,
    private loginService: LoginService,
    private masterDataService: MasterDataService,
    private snackBar: MatSnackBar,
    public dialogRef: MatDialogRef<InviteUserComponent>,
    @Inject(MAT_DIALOG_DATA) public data: InviteDialogData,
  ) {
    this.form = this.fb.group({
      email: ['', [Validators.required, Validators.email]],
    });
  }

  ngOnInit(): void {
    this.masterDataService.getMasterPermissionList().subscribe({
      next: (perms) => (this.permissions = perms),
      error: () =>
        this.snackBar.open('Failed to load permissions.', 'Close', {
          duration: 4000,
        }),
    });
  }

  /**
   * Toggle a permission ID in the selected set.
   * @param id - masterPermission primary key
   */
  togglePermission(id: number): void {
    if (this.selectedPermissionIds.has(id)) {
      this.selectedPermissionIds.delete(id);
    } else {
      this.selectedPermissionIds.add(id);
    }
  }

  isSelected(id: number): boolean {
    return this.selectedPermissionIds.has(id);
  }

  /**
   * Submit the invite — validates the form, calls the invite endpoint,
   * and displays the generated link on success.
   */
  onSendInvite(): void {
    if (this.form.invalid) return;
    if (this.data.isSuperAdmin && this.selectedOrgId === undefined) return;

    // Hard block: if any org-scoped permission is selected, an org must be resolved
    const hasOrgScopedSelected = Array.from(this.selectedPermissionIds).some(
      (id) => {
        const perm = this.permissions.find((p) => p.id === id);
        return perm ? this.isOrgScoped(perm) : false;
      },
    );

    if (hasOrgScopedSelected && !this.hasOrgContext) {
      this.error =
        'An organization must be selected when assigning org-scoped permissions.';
      return;
    }

    this.sending = true;
    this.error = '';

    const payload: any = {
      email: this.form.value.email,
      permission_ids: Array.from(this.selectedPermissionIds),
      org_id: this.data.targetOrgId ?? undefined,
    };

    if (this.data.isSuperAdmin) {
      payload.org_id = this.selectedOrgId;
    }

    this.loginService.invite(payload).subscribe({
      next: (res) => {
        this.sending = false;
        this.sent = true;
        this.inviteLink = res.data?.inviteLink ?? '';
      },
      error: (err) => {
        this.sending = false;
        this.error =
          err?.error?.message ?? 'Failed to send invite. Please try again.';
      },
    });
  }
  get hasOrgContext(): boolean {
    return !!(this.selectedOrgId || this.data.targetOrgId);
  }

  /**
   * Copy the invite link to the clipboard.
   */
  copyLink(): void {
    navigator.clipboard.writeText(this.inviteLink).then(() => {
      this.snackBar.open('Link copied to clipboard.', 'Close', {
        duration: 2000,
      });
    });
  }

  onClose(): void {
    this.dialogRef.close(this.sent);
  }
}
